import { useState, useMemo, useEffect, useCallback } from "react";
import useAppStore from "../../store/app.store";
import PageHeader from "../../components/ui/PageHeader";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

const moeda = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pct   = (a, b) => b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0;
const hoje  = () => new Date().toISOString().split("T")[0];
const ontem = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split("T")[0]; };
const mesAtual = () => new Date().toISOString().slice(0, 7);

const MOTIVOS_LABEL = {
  "Preco alto": "Preco alto", "Concorrente": "Concorrente", "Sem budget": "Sem budget",
  "Produto inadequado": "Produto inadequado", "Sem retorno do lead": "Sem retorno",
  "Prazo nao atende": "Prazo", "Ja tem fornecedor": "Ja tem fornecedor", "Outros": "Outros",
};

const Ico = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {(Array.isArray(d) ? d : [d]).map((p, i) => <path key={i} d={p} />)}
  </svg>
);

function Tabs({ aba, setAba, tabs }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #2A3652" }}>
      {tabs.map(t => (
        <button key={t.k} onClick={() => setAba(t.k)} style={{
          display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 14,
          fontWeight: aba === t.k ? 600 : 400, color: aba === t.k ? "#6C4CF5" : "#94A3B8",
          background: "none", border: "none", borderBottom: aba === t.k ? "2px solid #6C4CF5" : "2px solid transparent",
          cursor: "pointer", transition: "all .15s", marginBottom: -1,
        }}>
          {t.icon}{t.l}
        </button>
      ))}
    </div>
  );
}

function BarraProgresso({ valor, meta, label, cor = "#6C4CF5", formatarFn }) {
  const p = pct(valor, meta);
  const barCor = p >= 100 ? "#4ADE80" : p >= 70 ? cor : p >= 40 ? "#FBBF24" : "#F87171";
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "#94A3B8" }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: barCor }}>
          {formatarFn ? formatarFn(valor) : valor} / {formatarFn ? formatarFn(meta) : meta} ({p}%)
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,.06)" }}>
        <div style={{ height: "100%", borderRadius: 999, background: barCor, width: `${p}%`, transition: "width .4s" }} />
      </div>
    </div>
  );
}

function FunilMini({ leads, contatos, vendas }) {
  const etapas = [
    { label: "Leads", valor: leads, cor: "#6C4CF5" },
    { label: "Contatos", valor: contatos, cor: "#38BDF8", taxa: pct(contatos, leads) },
    { label: "Vendas", valor: vendas, cor: "#4ADE80", taxa: pct(vendas, contatos) },
  ];
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80, marginTop: 8 }}>
      {etapas.map((e, i) => {
        const maxH = 80;
        const h = leads > 0 ? Math.max(20, (e.valor / leads) * maxH) : 20;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 10, color: e.cor, fontWeight: 700 }}>{e.valor}</span>
            <div style={{ width: "100%", height: h, background: `${e.cor}28`, border: `1px solid ${e.cor}55`, borderRadius: 6 }} />
            <span style={{ fontSize: 9, color: "#64748B" }}>{e.label}</span>
            {e.taxa !== undefined && <span style={{ fontSize: 9, color: e.taxa >= 20 ? "#4ADE80" : "#F87171", fontWeight: 700 }}>{e.taxa}%</span>}
          </div>
        );
      })}
    </div>
  );
}

function FormMeta({ inicial, clientes, onSalvar, onFechar }) {
  const [f, setF] = useState(inicial || {
    cliente_id: "", mes: mesAtual(), leads_meta: "", contatos_meta: "",
    vendas_meta: "", receita_meta: "", ativo: true,
  });
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(f); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="form-label">Cliente *</label>
          <select className="form-input" value={f.cliente_id} onChange={e => s("cliente_id", e.target.value)} required>
            <option value="">Selecione...</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Mes de referencia *</label>
          <input className="form-input" type="month" value={f.mes} onChange={e => s("mes", e.target.value)} required />
        </div>
        <div>
          <label className="form-label">Meta de Leads</label>
          <input className="form-input" type="number" min="0" value={f.leads_meta} onChange={e => s("leads_meta", e.target.value)} placeholder="Ex: 300" />
        </div>
        <div>
          <label className="form-label">Meta de Contatos</label>
          <input className="form-input" type="number" min="0" value={f.contatos_meta} onChange={e => s("contatos_meta", e.target.value)} placeholder="Ex: 150" />
        </div>
        <div>
          <label className="form-label">Meta de Vendas *</label>
          <input className="form-input" type="number" min="0" value={f.vendas_meta} onChange={e => s("vendas_meta", e.target.value)} placeholder="Ex: 30" required />
        </div>
        <div>
          <label className="form-label">Meta de Receita (R$)</label>
          <input className="form-input" type="number" min="0" step="0.01" value={f.receita_meta} onChange={e => s("receita_meta", e.target.value)} placeholder="Ex: 50000" />
        </div>
      </div>
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" className="btn-primary flex-1">{inicial ? "Salvar" : "Criar Meta"}</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

function FormRegistroManual({ metas, clientes, onSalvar, onFechar }) {
  const [f, setF] = useState({ meta_id: "", cliente_id: "", data: hoje(), leads_recebidos: "", contatos: "", vendas: "", receita: "", motivos_perda: [], observacoes: "" });
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));

  const metasAtivas = metas.filter(m => m.ativo);
  const toggleMotivo = (m) => setF(p => ({
    ...p, motivos_perda: p.motivos_perda.includes(m) ? p.motivos_perda.filter(x => x !== m) : [...p.motivos_perda, m],
  }));

  const handleMetaChange = (metaId) => {
    const meta = metasAtivas.find(m => m.id === metaId);
    s("meta_id", metaId);
    if (meta) s("cliente_id", meta.cliente_id);
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(f); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="form-label">Meta / Cliente *</label>
          <select className="form-input" value={f.meta_id} onChange={e => handleMetaChange(e.target.value)} required>
            <option value="">Selecione...</option>
            {metasAtivas.map(m => {
              const cl = clientes.find(c => c.id === m.cliente_id);
              return <option key={m.id} value={m.id}>{cl?.nome_fantasia || cl?.razao_social || "?"} — {m.mes}</option>;
            })}
          </select>
        </div>
        <div className="col-span-2">
          <label className="form-label">Data *</label>
          <input className="form-input" type="date" value={f.data} onChange={e => s("data", e.target.value)} required />
        </div>
        <div><label className="form-label">Leads recebidos *</label><input className="form-input" type="number" min="0" value={f.leads_recebidos} onChange={e => s("leads_recebidos", e.target.value)} required /></div>
        <div><label className="form-label">Contatos</label><input className="form-input" type="number" min="0" value={f.contatos} onChange={e => s("contatos", e.target.value)} /></div>
        <div><label className="form-label">Vendas</label><input className="form-input" type="number" min="0" value={f.vendas} onChange={e => s("vendas", e.target.value)} /></div>
        <div><label className="form-label">Receita (R$)</label><input className="form-input" type="number" min="0" step="0.01" value={f.receita} onChange={e => s("receita", e.target.value)} /></div>
        <div className="col-span-2">
          <label className="form-label">Motivos de perda</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.keys(MOTIVOS_LABEL).map(m => (
              <button key={m} type="button" onClick={() => toggleMotivo(m)}
                style={{ fontSize: 11, padding: "3px 9px", borderRadius: 999, border: "none", cursor: "pointer",
                  background: f.motivos_perda.includes(m) ? "#F87171" : "rgba(255,255,255,.06)",
                  color: f.motivos_perda.includes(m) ? "#fff" : "#94A3B8" }}>{m}</button>
            ))}
          </div>
        </div>
        <div className="col-span-2">
          <label className="form-label">Observacoes</label>
          <textarea className="form-input" rows={2} value={f.observacoes} onChange={e => s("observacoes", e.target.value)} />
        </div>
      </div>
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" className="btn-primary flex-1">Salvar Registro</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

// ─── Visão Geral ──────────────────────────────────────────────────────────────

function AbaVisaoGeral({ metas, registros, clientes, verbas, setAba }) {
  const metasAtivas = metas.filter(m => m.ativo && m.mes === mesAtual());

  // Alertas: clientes que não enviaram o registro de ontem
  const alertas = metasAtivas.filter(m => {
    const ontemStr = ontem();
    return !registros.some(r => r.meta_id === m.id && r.data === ontemStr);
  });

  const totaisGeral = metasAtivas.map(m => {
    const regs = registros.filter(r => r.meta_id === m.id && r.data?.slice(0, 7) === m.mes);
    const totais = regs.reduce((acc, r) => ({
      leads: acc.leads + (r.leads_recebidos || 0),
      contatos: acc.contatos + (r.contatos || 0),
      vendas: acc.vendas + (r.vendas || 0),
      receita: acc.receita + (r.receita || 0),
    }), { leads: 0, contatos: 0, vendas: 0, receita: 0 });
    const cl = clientes.find(c => c.id === m.cliente_id);
    // ROI: pegar verba do mês de verbas_trafego
    const verbaMes = verbas.filter(v => v.cliente_id === m.cliente_id && v.mes_referencia === m.mes);
    const verbaTotal = verbaMes.reduce((a, v) => a + +((v.verba_google || 0)) + +((v.verba_meta || 0)), 0);
    const roi = verbaTotal > 0 ? ((totais.receita - verbaTotal) / verbaTotal * 100).toFixed(1) : null;
    return { m, cl, totais, roi, verbaTotal, alertaHoje: alertas.some(a => a.id === m.id) };
  });

  // Motivos de perda consolidados
  const motivosGeral = {};
  registros.filter(r => metasAtivas.some(m => m.id === r.meta_id)).forEach(r => {
    (r.motivos_perda || []).forEach(mot => { motivosGeral[mot] = (motivosGeral[mot] || 0) + 1; });
  });
  const motivosOrdenados = Object.entries(motivosGeral).sort((a, b) => b[1] - a[1]).slice(0, 5);

  const sumVendas   = totaisGeral.reduce((a, t) => a + t.totais.vendas, 0);
  const sumReceita  = totaisGeral.reduce((a, t) => a + t.totais.receita, 0);
  const sumLeads    = totaisGeral.reduce((a, t) => a + t.totais.leads, 0);
  const sumVerba    = totaisGeral.reduce((a, t) => a + t.verbaTotal, 0);
  const roiGeral    = sumVerba > 0 ? ((sumReceita - sumVerba) / sumVerba * 100).toFixed(1) : null;

  return (
    <div className="space-y-6">
      {/* Alertas de relatório */}
      {alertas.length > 0 && (
        <div style={{ background: "rgba(248,113,113,.08)", border: "1px solid rgba(248,113,113,.3)", borderRadius: 14, padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Ico d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" size={18} />
            <p style={{ fontSize: 13, fontWeight: 700, color: "#F87171" }}>
              {alertas.length} cliente{alertas.length > 1 ? "s" : ""} sem relatorio de {ontem()}
            </p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {alertas.map(m => {
              const cl = clientes.find(c => c.id === m.cliente_id);
              return (
                <span key={m.id} style={{ fontSize: 12, padding: "4px 10px", background: "rgba(248,113,113,.12)", color: "#F87171", borderRadius: 999, fontWeight: 600 }}>
                  {cl?.nome_fantasia || cl?.razao_social || "?"} — cobrar relatorio!
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* KPIs gerais */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: "Clientes Ativos", v: metasAtivas.length, cor: "#6C4CF5", icon: ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2","M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z","M23 21v-2a4 4 0 0 0-3-3.87","M16 3.13a4 4 0 0 1 0 7.75"] },
          { l: "Vendas no Mes", v: sumVendas, cor: "#4ADE80", icon: ["M22 12h-4l-3 9L9 3l-3 9H2"] },
          { l: "Receita Total", v: moeda(sumReceita), cor: "#FBBF24", icon: ["M12 1v22","M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"] },
          { l: "ROI Medio", v: roiGeral !== null ? `${roiGeral}%` : "—", cor: roiGeral >= 0 ? "#4ADE80" : "#F87171", icon: ["M3 3v18h18","M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"] },
        ].map((k, i) => (
          <div key={i} style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" }}>{k.l}</p>
              <span style={{ color: k.cor }}><Ico d={k.icon} size={16} /></span>
            </div>
            <p style={{ fontSize: 26, fontWeight: 700, color: k.cor, lineHeight: 1 }}>{k.v}</p>
          </div>
        ))}
      </div>

      {/* Cards por cliente */}
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>
          Clientes — {mesAtual()}
        </p>
        {totaisGeral.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#64748B" }}>
            <p style={{ fontSize: 14 }}>Nenhuma meta cadastrada para este mes.</p>
            <button onClick={() => setAba("metas")} style={{ marginTop: 12, padding: "8px 20px", background: "#6C4CF5", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}>
              Cadastrar Meta
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {totaisGeral.map(({ m, cl, totais, roi, verbaTotal, alertaHoje }) => (
              <div key={m.id} style={{ background: "#19243A", border: `1px solid ${alertaHoje ? "rgba(248,113,113,.35)" : "#2A3652"}`, borderRadius: 14, padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 700, color: "#E2E8F4" }}>{cl?.nome_fantasia || cl?.razao_social || "—"}</p>
                    <p style={{ fontSize: 11, color: "#64748B" }}>Meta: {m.vendas_meta} vendas · {moeda(m.receita_meta)}</p>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexDirection: "column", alignItems: "flex-end" }}>
                    {roi !== null && (
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: +roi >= 0 ? "rgba(74,222,128,.12)" : "rgba(248,113,113,.12)", color: +roi >= 0 ? "#4ADE80" : "#F87171", fontWeight: 700 }}>
                        ROI {+roi >= 0 ? "+" : ""}{roi}%
                      </span>
                    )}
                    {alertaHoje && (
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: "rgba(248,113,113,.12)", color: "#F87171", fontWeight: 700 }}>
                        Sem relatorio
                      </span>
                    )}
                  </div>
                </div>

                <BarraProgresso valor={totais.vendas} meta={m.vendas_meta} label="Vendas" cor="#4ADE80" />
                <BarraProgresso valor={totais.receita} meta={m.receita_meta} label="Receita" cor="#FBBF24" formatarFn={moeda} />

                {verbaTotal > 0 && (
                  <div style={{ display: "flex", gap: 12, marginBottom: 12, padding: "10px 12px", background: "rgba(255,255,255,.03)", borderRadius: 8 }}>
                    <div style={{ textAlign: "center", flex: 1 }}>
                      <p style={{ fontSize: 10, color: "#64748B", marginBottom: 2 }}>Verba Investida</p>
                      <p style={{ fontSize: 14, fontWeight: 700, color: "#94A3B8" }}>{moeda(verbaTotal)}</p>
                    </div>
                    <div style={{ width: 1, background: "#2A3652" }} />
                    <div style={{ textAlign: "center", flex: 1 }}>
                      <p style={{ fontSize: 10, color: "#64748B", marginBottom: 2 }}>Receita Gerada</p>
                      <p style={{ fontSize: 14, fontWeight: 700, color: "#FBBF24" }}>{moeda(totais.receita)}</p>
                    </div>
                    <div style={{ width: 1, background: "#2A3652" }} />
                    <div style={{ textAlign: "center", flex: 1 }}>
                      <p style={{ fontSize: 10, color: "#64748B", marginBottom: 2 }}>ROI</p>
                      <p style={{ fontSize: 14, fontWeight: 700, color: +roi >= 0 ? "#4ADE80" : "#F87171" }}>
                        {roi !== null ? `${+roi >= 0 ? "+" : ""}${roi}%` : "—"}
                      </p>
                    </div>
                  </div>
                )}

                <FunilMini leads={totais.leads} contatos={totais.contatos} vendas={totais.vendas} />

                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,.05)", display: "flex", gap: 8 }}>
                  <a href={`/c/${m.token}`} target="_blank" rel="noreferrer"
                    style={{ fontSize: 11, color: "#6C4CF5", background: "rgba(108,76,245,.1)", borderRadius: 6, padding: "4px 10px", textDecoration: "none" }}>
                    Abrir Link do Cliente
                  </a>
                  <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/c/${m.token}`); toast.success("Link copiado!"); }}
                    style={{ fontSize: 11, color: "#94A3B8", background: "rgba(255,255,255,.05)", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
                    Copiar Link
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Motivos de perda */}
      {motivosOrdenados.length > 0 && (
        <div style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 14, padding: 20 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: "#94A3B8", marginBottom: 14 }}>Top Motivos de Perda — {mesAtual()}</h3>
          {motivosOrdenados.map(([mot, qtd], i) => {
            const max = motivosOrdenados[0][1];
            return (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "#94A3B8" }}>{mot}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#F87171" }}>{qtd}x</span>
                </div>
                <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,.06)" }}>
                  <div style={{ height: "100%", borderRadius: 999, background: "#F87171", width: `${(qtd / max) * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Aba Metas ───────────────────────────────────────────────────────────────

function AbaMetas({ metas, clientes }) {
  const addMeta    = useAppStore(s => s.addMetaComercial);
  const updateMeta = useAppStore(s => s.updateMetaComercial);
  const deleteMeta = useAppStore(s => s.deleteMetaComercial);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroMes, setFiltroMes] = useState(mesAtual());

  const filtradas = metas.filter(m => !filtroMes || m.mes === filtroMes);

  const salvar = dados => {
    if (editando) { updateMeta(editando.id, dados); toast.success("Meta atualizada!"); setEditando(null); }
    else { addMeta(dados); toast.success("Meta criada!"); setModal(false); }
  };

  const copiarLink = (m) => {
    navigator.clipboard.writeText(`${window.location.origin}/c/${m.token}`);
    toast.success("Link copiado! Envie para o comercial do cliente.");
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 18, alignItems: "center" }}>
        <input type="month" className="form-input" style={{ width: "auto" }} value={filtroMes} onChange={e => setFiltroMes(e.target.value)} />
        <button onClick={() => setModal(true)} className="btn-primary" style={{ marginLeft: "auto" }}>+ Nova Meta</button>
      </div>

      {filtradas.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#64748B" }}>
          <p style={{ fontSize: 14 }}>Nenhuma meta cadastrada para este periodo.</p>
        </div>
      ) : (
        <div style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 14, overflow: "hidden" }}>
          <div className="resp-table-wrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #2A3652", background: "rgba(255,255,255,.02)" }}>
                {["Cliente", "Mes", "Meta Leads", "Meta Vendas", "Meta Receita", "Status", "Link", "Acoes"].map((h, i) => (
                  <th key={i} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtradas.map(m => {
                const cl = clientes.find(c => c.id === m.cliente_id);
                return (
                  <tr key={m.id} style={{ borderBottom: "1px solid rgba(42,54,82,.5)" }}>
                    <td style={{ padding: "12px 16px", color: "#E2E8F4", fontWeight: 500 }}>{cl?.nome_fantasia || cl?.razao_social || "—"}</td>
                    <td style={{ padding: "12px 16px", color: "#94A3B8" }}>{m.mes}</td>
                    <td style={{ padding: "12px 16px", color: "#6C4CF5", fontWeight: 700 }}>{m.leads_meta || "—"}</td>
                    <td style={{ padding: "12px 16px", color: "#4ADE80", fontWeight: 700 }}>{m.vendas_meta}</td>
                    <td style={{ padding: "12px 16px", color: "#FBBF24", fontWeight: 700 }}>{m.receita_meta ? moeda(m.receita_meta) : "—"}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <button onClick={() => updateMeta(m.id, { ativo: !m.ativo })}
                        style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, border: "none", cursor: "pointer",
                          background: m.ativo ? "rgba(74,222,128,.12)" : "rgba(148,163,184,.12)",
                          color: m.ativo ? "#4ADE80" : "#94A3B8", fontWeight: 700 }}>
                        {m.ativo ? "Ativa" : "Inativa"}
                      </button>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <button onClick={() => copiarLink(m)} style={{ fontSize: 11, color: "#38BDF8", background: "rgba(56,189,248,.1)", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
                        Copiar Link
                      </button>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => setEditando(m)} style={{ fontSize: 12, color: "#6C4CF5", background: "none", border: "none", cursor: "pointer" }}>Editar</button>
                        <button onClick={() => { if (window.confirm("Excluir meta?")) { deleteMeta(m.id); toast.success("Excluida!"); } }}
                          style={{ fontSize: 12, color: "#F87171", background: "none", border: "none", cursor: "pointer" }}>Excluir</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal open={modal || !!editando} onClose={() => { setModal(false); setEditando(null); }} title={editando ? "Editar Meta" : "Nova Meta Comercial"} size="md">
        <FormMeta inicial={editando} clientes={clientes} onSalvar={salvar} onFechar={() => { setModal(false); setEditando(null); }} />
      </Modal>
    </div>
  );
}

// ─── Aba Registros ────────────────────────────────────────────────────────────

function AbaRegistros({ registros, metas, clientes }) {
  const addRegistro    = useAppStore(s => s.addRegistroComercial);
  const deleteRegistro = useAppStore(s => s.deleteRegistroComercial);
  const [modal, setModal] = useState(false);
  const [filtroMes, setFiltroMes] = useState(mesAtual());
  const [filtroCliente, setFiltroCliente] = useState("");

  const filtrados = useMemo(() => {
    return registros
      .filter(r => {
        const meta = metas.find(m => m.id === r.meta_id);
        if (!meta) return false;
        if (filtroMes && r.data?.slice(0, 7) !== filtroMes) return false;
        if (filtroCliente && meta.cliente_id !== filtroCliente) return false;
        return true;
      })
      .sort((a, b) => b.data.localeCompare(a.data));
  }, [registros, metas, filtroMes, filtroCliente]);

  const salvar = dados => {
    addRegistro(dados);
    toast.success("Registro salvo!");
    setModal(false);
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <input type="month" className="form-input" style={{ width: "auto" }} value={filtroMes} onChange={e => setFiltroMes(e.target.value)} />
        <select className="form-input" style={{ width: "auto" }} value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Todos clientes</option>
          {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
        </select>
        <button onClick={() => setModal(true)} className="btn-primary" style={{ marginLeft: "auto" }}>+ Entrada Manual</button>
      </div>

      {filtrados.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#64748B" }}>
          <p style={{ fontSize: 14 }}>Nenhum registro encontrado.</p>
        </div>
      ) : (
        <div style={{ background: "#19243A", border: "1px solid #2A3652", borderRadius: 14, overflow: "hidden" }}>
          <div className="resp-table-wrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #2A3652", background: "rgba(255,255,255,.02)" }}>
                {["Data", "Cliente", "Leads", "Contatos", "Vendas", "Receita", "Conv.", "Motivos Perda", ""].map((h, i) => (
                  <th key={i} style={{ padding: "10px 14px", textAlign: i === 0 || i === 1 ? "left" : "center", fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.map(r => {
                const meta = metas.find(m => m.id === r.meta_id);
                const cl   = clientes.find(c => c.id === meta?.cliente_id);
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid rgba(42,54,82,.4)" }}>
                    <td style={{ padding: "10px 14px", color: "#E2E8F4" }}>{r.data}</td>
                    <td style={{ padding: "10px 14px", color: "#94A3B8" }}>{cl?.nome_fantasia || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#6C4CF5", fontWeight: 700, textAlign: "center" }}>{r.leads_recebidos}</td>
                    <td style={{ padding: "10px 14px", color: "#38BDF8", fontWeight: 700, textAlign: "center" }}>{r.contatos || 0}</td>
                    <td style={{ padding: "10px 14px", color: "#4ADE80", fontWeight: 700, textAlign: "center" }}>{r.vendas || 0}</td>
                    <td style={{ padding: "10px 14px", color: "#FBBF24", fontWeight: 700, textAlign: "center" }}>{moeda(r.receita)}</td>
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: pct(r.vendas, r.leads_recebidos) >= 20 ? "#4ADE80" : "#F87171" }}>
                        {pct(r.vendas, r.leads_recebidos)}%
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", maxWidth: 180 }}>
                      {(r.motivos_perda || []).length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {r.motivos_perda.map((m, i) => (
                            <span key={i} style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "rgba(248,113,113,.12)", color: "#F87171" }}>
                              {MOTIVOS_LABEL[m] || m}
                            </span>
                          ))}
                        </div>
                      ) : <span style={{ fontSize: 11, color: "#3d4f72" }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <button onClick={() => { if (window.confirm("Excluir registro?")) { deleteRegistro(r.id); toast.success("Excluido!"); } }}
                        style={{ fontSize: 11, color: "#F87171", background: "rgba(248,113,113,.1)", border: "none", borderRadius: 6, padding: "3px 8px", cursor: "pointer" }}>
                        Excluir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Entrada Manual de Dados" size="md">
        <FormRegistroManual metas={metas} clientes={clientes} onSalvar={salvar} onFechar={() => setModal(false)} />
      </Modal>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Comercial() {
  const metas    = useAppStore(s => s.metas_comerciais);
  const registros = useAppStore(s => s.registros_comerciais);
  const clientes  = useAppStore(s => s.clientes);
  const verbas    = useAppStore(s => s.verbas_trafego);
  const [aba, setAba] = useState("overview");

  // Verificar alertas de relatório no mount
  useEffect(() => {
    const metasAtivas = metas.filter(m => m.ativo && m.mes === mesAtual());
    const semRelatorio = metasAtivas.filter(m => !registros.some(r => r.meta_id === m.id && r.data === ontem()));
    if (semRelatorio.length > 0) {
      const nomes = semRelatorio.map(m => {
        const cl = clientes.find(c => c.id === m.cliente_id);
        return cl?.nome_fantasia || cl?.razao_social || "?";
      });
      toast.error(`Sem relatorio de ontem: ${nomes.join(", ")}`, { duration: 6000, id: "alerta-relatorio" });
    }
  }, []);

  const TABS = [
    { k: "overview",  l: "Visao Geral",  icon: <Ico d={["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z","M9 22V12h6v10"]} size={14} /> },
    { k: "metas",     l: "Metas",        icon: <Ico d={["M12 20h9","M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"]} size={14} /> },
    { k: "registros", l: "Registros",    icon: <Ico d={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M16 13H8","M16 17H8","M10 9H8"]} size={14} /> },
  ];

  return (
    <div>
      <PageHeader title="Comercial" subtitle="Acompanhamento de funil de vendas, metas e ROI por cliente" />
      <Tabs aba={aba} setAba={setAba} tabs={TABS} />
      {aba === "overview"  && <AbaVisaoGeral metas={metas} registros={registros} clientes={clientes} verbas={verbas} setAba={setAba} />}
      {aba === "metas"     && <AbaMetas metas={metas} clientes={clientes} />}
      {aba === "registros" && <AbaRegistros registros={registros} metas={metas} clientes={clientes} />}
    </div>
  );
}
